import React, { useEffect, useRef, useState } from "react";
import Select from "react-select";
import { Switch } from "@mui/material";
import { FaTrash } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "./AddItem.css";
import APIURL from "../api/apiClient";
import VariantEditorSection from "../components/VariantEditorSection";
import {
  buildVariantPayload,
  createEmptyVariant,
  normalizeVariantList,
  updateVariantDraft,
  validateVariants,
} from "../utils/itemVariants";

const initialFormState = {
  name: "",
  brand: "",
  thumbnail: null,
  thumbnailPreview: "",
  images: [],
  imagePreviews: [],
  description: "",
  category: "",
  category_id: [],
  actual_price: "",
  selling_price: "",
  tax_percentage: "",
  discount: "",
  status: 1,
  stock: "",
  moq: "",
  approved: "",
  refurbished: "",
  net_weight: "",
  dimensions: "",
};

const AddItem = () => {
  const [formData, setFormData] = useState(initialFormState);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [errors, setErrors] = useState({});
  const [variants, setVariants] = useState([]);
  const [variantErrors, setVariantErrors] = useState({});
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const calculateDiscount = (actual, selling) => {
    if (!actual || Number(actual) <= 0 || !selling) {
      return "";
    }

    return Math.round(((Number(actual) - Number(selling)) / Number(actual)) * 100).toString();
  };

  const calculateSellingPrice = (actual, discount) => {
    if (!actual || Number(actual) <= 0 || discount === "") {
      return "";
    }

    return (Number(actual) - (Number(actual) * Number(discount)) / 100).toFixed(2);
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.brand.trim()) {
      newErrors.brand = "Brand is required";
    }

    if (formData.images.length > 5) {
      newErrors.images = "You can only upload 5 images";
    }

    if (!selectedCategory) {
      newErrors.category = "Please select a main category";
    }

    if (selectedCategory && subCategories.length > 0 && !selectedSubCategory) {
      newErrors.category_id = "Please select a sub category";
    }

    if (!formData.actual_price || Number(formData.actual_price) <= 0) {
      newErrors.actual_price = "Actual price must be a positive number";
    }

    if (!formData.selling_price || Number(formData.selling_price) <= 0) {
      newErrors.selling_price = "Selling price must be a positive number";
    }

    if (formData.tax_percentage === "" || Number.isNaN(Number(formData.tax_percentage))) {
      newErrors.tax_percentage = "Please enter a valid tax percentage";
    }

    if (formData.discount === "" || Number.isNaN(Number(formData.discount))) {
      newErrors.discount = "Please enter a valid discount";
    }

    const currentVariantErrors = validateVariants(variants);

    setErrors(newErrors);
    setVariantErrors(currentVariantErrors);

    return Object.keys(newErrors).length === 0 && Object.keys(currentVariantErrors).length === 0;
  };

  const handleChange = (event) => {
    const { name, type, checked, files, value } = event.target;

    if (type === "checkbox") {
      setFormData((previous) => ({
        ...previous,
        [name]: checked ? 1 : 0,
      }));
      return;
    }

    if (type === "file") {
      if (name === "thumbnail" && files?.[0]) {
        const file = files[0];
        setFormData((previous) => ({
          ...previous,
          thumbnail: file,
          thumbnailPreview: URL.createObjectURL(file),
        }));
      }

      if (name === "images") {
        const selectedFiles = Array.from(files || []);
        setFormData((previous) => {
          const nextImages = [...previous.images, ...selectedFiles].slice(0, 5);
          return {
            ...previous,
            images: nextImages,
            imagePreviews: nextImages.map((file) => URL.createObjectURL(file)),
          };
        });

        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }

      return;
    }

    setFormData((previous) => {
      const updated = { ...previous, [name]: value };

      if (name === "actual_price" || name === "selling_price") {
        updated.discount = calculateDiscount(updated.actual_price, updated.selling_price);
      }

      if (name === "discount") {
        const discountNumber = Number(value);
        updated.discount = Number.isFinite(discountNumber) ? Math.round(discountNumber).toString() : "";
      }

      if (name === "actual_price" || name === "discount") {
        updated.selling_price = calculateSellingPrice(updated.actual_price, updated.discount);
      }

      return updated;
    });
  };

  const handleDeleteImage = (indexToDelete) => {
    setFormData((previous) => {
      const nextImages = previous.images.filter((_, index) => index !== indexToDelete);
      return {
        ...previous,
        images: nextImages,
        imagePreviews: nextImages.map((file) => URL.createObjectURL(file)),
      };
    });
  };

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await APIURL.get("/fetchcategories.php");
        const parsed = Array.isArray(response) ? response : JSON.parse(response);

        setCategories(
          parsed.map((category) => ({
            value: category.id,
            label: category.name,
          }))
        );
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    if (!selectedCategory) {
      setSubCategories([]);
      setSelectedSubCategory(null);
      return;
    }

    const fetchSubCategories = async () => {
      try {
        const response = await APIURL.get(`/subCategories.php?category_id=${selectedCategory.value}`);

        if (response.status && Array.isArray(response.data)) {
          setSubCategories(
            response.data.map((subCategory) => ({
              value: subCategory.id,
              label: subCategory.name,
            }))
          );
        } else {
          setSubCategories([]);
        }
      } catch (error) {
        console.error("Error fetching subcategories:", error);
        setSubCategories([]);
      }
    };

    fetchSubCategories();
  }, [selectedCategory]);

  const handleMainCategoryChange = (option) => {
    setSelectedCategory(option);
    setSelectedSubCategory(null);
    setFormData((previous) => ({
      ...previous,
      category: option ? option.value : "",
      category_id: [],
    }));
  };

  const handleSubCategoryChange = (option) => {
    setSelectedSubCategory(option);
    setFormData((previous) => ({
      ...previous,
      category_id: option ? [option.value] : [],
    }));
  };

  const handleDescriptionChange = (content) => {
    setFormData((previous) => ({
      ...previous,
      description: content,
    }));
  };

  const updateVariantsState = (updater) => {
    setVariants((previous) => normalizeVariantList(updater(previous)));
    setVariantErrors({});
  };

  const handleAddVariant = () => {
    updateVariantsState((previous) => [
      ...previous,
      createEmptyVariant({
        is_default: previous.length === 0 ? 1 : 0,
        sort_order: String(previous.length + 1),
      }),
    ]);
  };

  const handleRemoveVariant = (clientId) => {
    updateVariantsState((previous) => previous.filter((variant) => variant.client_id !== clientId));
  };

  const handleVariantFieldChange = (clientId, field, value) => {
    updateVariantsState((previous) =>
      previous.map((variant) =>
        variant.client_id === clientId ? updateVariantDraft(variant, field, value) : variant
      )
    );
  };

  const handleVariantImageChange = (clientId, file) => {
    updateVariantsState((previous) =>
      previous.map((variant) =>
        variant.client_id === clientId
          ? {
              ...variant,
              image: file,
              imagePreview: file ? URL.createObjectURL(file) : variant.imagePreview,
            }
          : variant
      )
    );
  };

  const handleClearVariantImage = (clientId) => {
    updateVariantsState((previous) =>
      previous.map((variant) =>
        variant.client_id === clientId
          ? {
              ...variant,
              image: null,
              existing_image: "",
              imagePreview: "",
            }
          : variant
      )
    );
  };

  const handleVariantGalleryChange = (clientId, files) => {
    updateVariantsState((previous) =>
      previous.map((variant) => {
        if (variant.client_id !== clientId) {
          return variant;
        }

        const remainingSlots = Math.max(
          0,
          5 - variant.existing_images.length - variant.galleryFiles.length
        );
        const acceptedFiles = files.slice(0, remainingSlots);

        return {
          ...variant,
          galleryFiles: [...variant.galleryFiles, ...acceptedFiles],
          galleryPreviews: [
            ...variant.galleryPreviews,
            ...acceptedFiles.map((file) => URL.createObjectURL(file)),
          ],
        };
      })
    );
  };

  const handleDeleteVariantGalleryImage = (clientId, imageIndex) => {
    updateVariantsState((previous) =>
      previous.map((variant) => {
        if (variant.client_id !== clientId) {
          return variant;
        }

        if (imageIndex < variant.existing_images.length) {
          return {
            ...variant,
            existing_images: variant.existing_images.filter((_, index) => index !== imageIndex),
            galleryImages: variant.galleryImages.filter((_, index) => index !== imageIndex),
          };
        }

        const previewIndex = imageIndex - variant.existing_images.length;
        return {
          ...variant,
          galleryFiles: variant.galleryFiles.filter((_, index) => index !== previewIndex),
          galleryPreviews: variant.galleryPreviews.filter((_, index) => index !== previewIndex),
        };
      })
    );
  };

  const handleSetDefaultVariant = (clientId) => {
    updateVariantsState((previous) =>
      previous.map((variant) => ({
        ...variant,
        is_default: variant.client_id === clientId ? 1 : 0,
      }))
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      const requestData = new FormData();

      Object.entries(formData).forEach(([key, value]) => {
        if (key === "images") {
          value.forEach((image) => {
            if (image instanceof File) {
              requestData.append("images[]", image);
            }
          });
          return;
        }

        if (key === "thumbnail") {
          if (value instanceof File) {
            requestData.append("thumbnail", value);
          }
          return;
        }

        if (key === "thumbnailPreview" || key === "imagePreviews" || key === "category" || key === "category_id") {
          return;
        }

        requestData.append(key, value ?? "");
      });

      if (selectedCategory) {
        requestData.append("category", selectedCategory.value);
      }

      requestData.append(
        "category_id",
        JSON.stringify(selectedSubCategory ? [selectedSubCategory.value] : [])
      );

      const variantPayload = buildVariantPayload(variants);
      variantPayload.forEach((variantPayloadItem) => {
        const matchingVariant = variants.find(
          (variant) => variant.client_id === variantPayloadItem.client_id
        );

        if (variantPayloadItem.image_upload_key && matchingVariant?.image instanceof File) {
          requestData.append(variantPayloadItem.image_upload_key, matchingVariant.image);
        }

        (variantPayloadItem.gallery_upload_keys || []).forEach((uploadKey, imageIndex) => {
          const galleryFile = matchingVariant?.galleryFiles?.[imageIndex];
          if (galleryFile instanceof File) {
            requestData.append(uploadKey, galleryFile);
          }
        });
      });

      requestData.append(
        "variants",
        JSON.stringify(variantPayload.map(({ client_id, ...variant }) => variant))
      );

      const response = await APIURL.post("/insertitems.php", requestData, true);

      if (response.success) {
        message.success("Item Added Successfully");
        setFormData(initialFormState);
        setSelectedCategory(null);
        setSelectedSubCategory(null);
        setSubCategories([]);
        setErrors({});
        setVariants([]);
        setVariantErrors({});
        setErrorMessage("");
        navigate("/items");
        return;
      }

      setErrorMessage(response.error || response.message || "Submission failed");
    } catch (error) {
      console.error("Error submitting form:", error);
      setErrorMessage(error.message || "Submission failed. Please try again.");
    }
  };

  return (
    <div className="form-container">
      <h2 className="form-title">Add Item</h2>
      <form onSubmit={handleSubmit} encType="multipart/form-data" className="form-group">
        <div className="form-grid">
          <div className="form-fields">
            <div className="name-brand">
              <label htmlFor="name" className="label">
                Name
              </label>
              <input
                type="text"
                id="name"
                name="name"
                placeholder="Enter name"
                className="form-control input"
                value={formData.name}
                onChange={handleChange}
                required
              />
              {errors.name ? <div className="error-message">{errors.name}</div> : null}

              <label htmlFor="brand" className="label">
                Brand
              </label>
              <input
                type="text"
                id="brand"
                name="brand"
                placeholder="Enter your brand"
                className="form-control input"
                value={formData.brand}
                onChange={handleChange}
                required
              />
              {errors.brand ? <div className="error-message">{errors.brand}</div> : null}
            </div>

            <label htmlFor="description" className="label">
              Description
            </label>
            <ReactQuill
              theme="snow"
              value={formData.description}
              onChange={handleDescriptionChange}
              placeholder="Enter description"
              style={{ marginBottom: "50px" }}
            />

            <label htmlFor="category" className="label">
              Main Category
            </label>
            <Select
              name="category"
              className="custom-select select"
              placeholder="Select Main Category"
              onChange={handleMainCategoryChange}
              options={categories}
              value={selectedCategory}
            />
            {errors.category ? <div className="error-message">{errors.category}</div> : null}

            <label htmlFor="subcategory_id" className="label">
              Sub Category
            </label>
            <Select
              name="category_id"
              className="custom-select select"
              options={
                selectedCategory
                  ? subCategories.length > 0
                    ? subCategories
                    : [{ value: "", label: "No subcategories available", isDisabled: true }]
                  : []
              }
              onChange={handleSubCategoryChange}
              value={selectedSubCategory}
              placeholder={selectedCategory ? "Select Subcategory" : "Select Main Category first"}
              isDisabled={!selectedCategory}
            />
            {errors.category_id ? <div className="error-message">{errors.category_id}</div> : null}

            <label htmlFor="actual_price" className="label">
              Actual Price
            </label>
            <input
              type="number"
              id="actual_price"
              name="actual_price"
              placeholder="Enter actual price"
              className="form-control input"
              value={formData.actual_price}
              onChange={handleChange}
              required
            />
            {errors.actual_price ? <div className="error-message">{errors.actual_price}</div> : null}

            <label htmlFor="selling_price" className="label">
              Selling Price
            </label>
            <input
              type="number"
              id="selling_price"
              name="selling_price"
              placeholder="Enter selling price"
              className="form-control input"
              value={formData.selling_price}
              onChange={handleChange}
              required
            />
            {errors.selling_price ? (
              <div className="error-message">{errors.selling_price}</div>
            ) : null}

            <label htmlFor="tax_percentage" className="label">
              Tax Percentage (%)
            </label>
            <input
              type="number"
              id="tax_percentage"
              name="tax_percentage"
              placeholder="Enter tax percentage"
              className="form-control input"
              value={formData.tax_percentage}
              onChange={handleChange}
              required
            />
            {errors.tax_percentage ? (
              <div className="error-message">{errors.tax_percentage}</div>
            ) : null}

            <div className="switch-container">
              <span className="switch-label">Status</span>
              <Switch
                checked={Boolean(formData.status)}
                onChange={(event) =>
                  handleChange({
                    target: {
                      name: "status",
                      type: "checkbox",
                      checked: event.target.checked,
                    },
                  })
                }
                name="status"
              />
            </div>

            <label htmlFor="discount" className="label">
              Discount (%)
            </label>
            <input
              type="number"
              id="discount"
              name="discount"
              placeholder="Enter discount"
              className="form-control input"
              value={formData.discount}
              onChange={handleChange}
              required
            />
            {errors.discount ? <div className="error-message">{errors.discount}</div> : null}
          </div>

          <div className="form-sidebar">
            <div className="image-upload-card">
              <h3 className="section-title">Images</h3>

              <div className="thumbnail-upload">
                <label htmlFor="thumbnail" className="label-thumbnail">
                  Add <br />
                  Thumbnail
                  <input
                    type="file"
                    id="thumbnail"
                    name="thumbnail"
                    accept="image/png, image/jpg, image/jpeg, image/webp"
                    onChange={handleChange}
                  />
                </label>
                {formData.thumbnailPreview ? (
                  <div className="thumbnail-preview">
                    <img
                      src={formData.thumbnailPreview}
                      alt="thumbnail-preview"
                      className="thumbnail-img"
                    />
                  </div>
                ) : null}
              </div>

              <div className="additional-images">
                <div className="images-upload-container">
                  <label className="images-label" htmlFor="images">
                    + Add Images <br /> <span>Up to 5 images</span>
                    <input
                      type="file"
                      name="images"
                      accept="image/*"
                      id="images"
                      multiple
                      onChange={handleChange}
                      ref={fileInputRef}
                    />
                  </label>

                  <div className="images-preview">
                    {formData.imagePreviews.map((image, index) => (
                      <div key={`${image}-${index}`} className="image-preview-item">
                        <img src={image} alt="preview" className="preview-img" />
                        <button
                          type="button"
                          onClick={() => handleDeleteImage(index)}
                          className="delete-button"
                        >
                          <FaTrash className="trash-icon" />
                        </button>
                        <span className="image-number">{index + 1}</span>
                      </div>
                    ))}
                  </div>
                </div>
                {errors.images ? <div className="error-message">{errors.images}</div> : null}
              </div>
            </div>

            <div className="additional-fields-card">
              <h3 className="section-title">Additional Details</h3>

              <label htmlFor="stock" className="label">
                Stock
              </label>
              <input
                type="number"
                id="stock"
                name="stock"
                placeholder="Enter stock"
                className="form-control input"
                value={formData.stock}
                onChange={handleChange}
              />

              <label htmlFor="moq" className="label">
                Maximum Order Quantity
              </label>
              <input
                type="number"
                id="moq"
                name="moq"
                placeholder="Enter MOQ"
                className="form-control input"
                value={formData.moq}
                onChange={handleChange}
              />

              <label htmlFor="approved" className="label">
                Approved
              </label>
              <select
                id="approved"
                name="approved"
                className="custom-select select"
                value={formData.approved}
                onChange={handleChange}
              >
                <option value="" disabled>
                  Select Option
                </option>
                <option value="1">Yes</option>
                <option value="0">No</option>
              </select>

              <label htmlFor="refurbished" className="label">
                Refurbished
              </label>
              <select
                id="refurbished"
                name="refurbished"
                className="custom-select select"
                value={formData.refurbished}
                onChange={handleChange}
              >
                <option value="" disabled>
                  Select Option
                </option>
                <option value="1">Yes</option>
                <option value="0">No</option>
              </select>

              <label htmlFor="net_weight" className="label">
                Net Weight
              </label>
              <input
                type="text"
                id="net_weight"
                name="net_weight"
                placeholder="Enter Net Weight"
                className="form-control input"
                value={formData.net_weight}
                onChange={handleChange}
              />

              <label htmlFor="dimensions" className="label">
                Dimensions
              </label>
              <input
                type="text"
                id="dimensions"
                name="dimensions"
                placeholder="Enter Dimensions"
                className="form-control input"
                value={formData.dimensions}
                onChange={handleChange}
              />
            </div>
          </div>
        </div>

        <VariantEditorSection
          variants={variants}
          errors={variantErrors}
          onAddVariant={handleAddVariant}
          onRemoveVariant={handleRemoveVariant}
          onVariantFieldChange={handleVariantFieldChange}
          onVariantImageChange={handleVariantImageChange}
          onVariantGalleryChange={handleVariantGalleryChange}
          onDeleteVariantGalleryImage={handleDeleteVariantGalleryImage}
          onClearVariantImage={handleClearVariantImage}
          onSetDefaultVariant={handleSetDefaultVariant}
        />

        {errorMessage ? <div className="error-message">{errorMessage}</div> : null}

        <button type="submit" name="submit" className="btn btn-primary submit-button">
          Submit
        </button>
      </form>
    </div>
  );
};

export default AddItem;
